import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Shield, Download, Trash2, Loader2, Cookie, Mail, Phone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function PrivacyCenter() {
  const { user } = useAuth();
  const { data, refetch } = useQuery({ queryKey: ["my-consent"], queryFn: () => api.get("/privacy/consent").then((r) => r.data), enabled: !!user });
  const [c, setC] = useState({ analytics: false, marketing: false, email_opt_in: false, sms_opt_in: false });
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reason, setReason] = useState("");
  useEffect(() => { if (data) setC(data); }, [data]);

  if (!user) return <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#4A5D6B]">Please sign in to manage your privacy settings.</div>;

  const save = async () => {
    setSaving(true);
    try { await api.put("/privacy/consent", c); toast.success("Preferences saved"); refetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/privacy/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "my-enhancing-your-world-data.json"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };
  const requestDelete = async () => {
    if (!window.confirm("Request deletion of your account and data? Our team will process this and confirm.")) return;
    try { await api.post("/privacy/delete-request", { detail: reason }); toast.success("Deletion request submitted. We'll be in touch."); setReason(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const Toggle = ({ k, label, desc, icon: Icon, locked }) => (
    <label className="flex items-start justify-between gap-4 py-3 border-b border-[#E5E0D8] last:border-0">
      <div className="flex items-start gap-3">
        {Icon && <Icon size={17} className="text-[#144F75] mt-0.5" />}
        <div><div className="text-sm font-medium text-[#1A242B]">{label}</div><div className="text-xs text-[#4A5D6B]">{desc}</div></div>
      </div>
      <input type="checkbox" data-testid={`consent-${k}`} checked={locked ? true : c[k]} disabled={locked} onChange={(e) => setC({ ...c, [k]: e.target.checked })} className="mt-1 accent-[#144F75]" />
    </label>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="privacy-center">
      <div className="flex items-center gap-2 mb-2"><Shield size={24} className="text-[#144F75]" /><h1 className="font-serif text-4xl text-[#1A242B]">Privacy Center</h1></div>
      <p className="text-[#4A5D6B] mb-10">Manage how your data is used, download a copy, or request deletion.</p>

      <div className="eyw-card p-8 mb-8">
        <h2 className="font-serif text-2xl text-[#1A242B] mb-2 flex items-center gap-2"><Cookie size={20} /> Consent preferences</h2>
        <Toggle k="necessary" label="Strictly necessary" desc="Required to run the site — always on." locked />
        <Toggle k="analytics" label="Analytics" desc="Anonymous usage stats to improve the experience." />
        <Toggle k="marketing" label="Marketing" desc="Personalized offers and content." />
        <Toggle k="email_opt_in" label="Email updates" desc="Newsletters, event invites, digests." icon={Mail} />
        <Toggle k="sms_opt_in" label="SMS reminders" desc="Appointment reminders by text." icon={Phone} />
        <div className="flex justify-end mt-4"><button onClick={save} disabled={saving} data-testid="consent-save" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Save preferences</button></div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="eyw-card p-8">
          <h2 className="font-serif text-xl text-[#1A242B] mb-2 flex items-center gap-2"><Download size={18} /> Export my data</h2>
          <p className="text-sm text-[#4A5D6B] mb-4">Download everything we hold about you as a JSON file.</p>
          <button onClick={exportData} disabled={exporting} data-testid="privacy-export" className="eyw-btn !py-2 text-sm disabled:opacity-60">{exporting && <Loader2 className="animate-spin" size={15} />} Download my data</button>
        </div>
        <div className="eyw-card p-8">
          <h2 className="font-serif text-xl text-[#1A242B] mb-2 flex items-center gap-2 text-red-600"><Trash2 size={18} /> Delete my data</h2>
          <p className="text-sm text-[#4A5D6B] mb-3">Request deletion of your account and personal data.</p>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="privacy-delete-reason" className="eyw-input w-full mb-3 text-sm" />
          <button onClick={requestDelete} data-testid="privacy-delete-request" className="eyw-btn-ghost !py-2 text-sm !border-red-300 !text-red-600 hover:!bg-red-500">Request deletion</button>
        </div>
      </div>
    </div>
  );
}
